import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Alert,
  Box,
  InputAdornment,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import HotelIcon from "@mui/icons-material/Hotel";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import LocalTaxiIcon from "@mui/icons-material/LocalTaxi";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";

import {
  IPD_COLORS,
  IPD_SHAPE,
  ipdCardSx,
  ipdPageSx,
  ipdElevatedInputSx,
  IpdButton,
  IpdPageHeader,
  IpdStatCard,
} from "../../../ipd/utils";
import {
  MlcBadge,
  EmergencyPlaceholder,
  StageBadge,
  TriageBadge,
  TriageStripe,
} from "../../components/EmergencyBadges";
import {
  ARRIVAL_MODE_LABEL,
  BOARD_REFRESH_MS,
  triageRank,
  visitStage,
  waitingSince,
} from "../../constants";
import { listEmergencyVisits } from "../../api/emergencyApi";
import TriageDialog from "../../components/TriageDialog";
import AssignBedDialog from "../../components/AssignBedDialog";

const COLUMNS = "minmax(190px, 1.6fr) 116px 100px minmax(120px, 1fr) 86px 128px 148px";

function BoardColumnHeader() {
  return (
    <Box
      sx={{
        display: { xs: "none", md: "grid" },
        gridTemplateColumns: COLUMNS,
        gap: 2,
        px: 2.5,
        pb: 1,
        pl: 3,
      }}
    >
      {["Patient", "Triage", "Bed", "Provider", "Waiting", "Stage", ""].map((h, i) => (
        <Typography
          key={h || `spacer-${i}`}
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            color: IPD_COLORS.textSecondary,
          }}
        >
          {h}
        </Typography>
      ))}
    </Box>
  );
}

function BoardRow({ visit, onOpen, onTriage, onAssignBed }) {
  const stage = visitStage(visit);
  const ArrivalIcon = visit.arrival_mode === "ambulance" ? LocalTaxiIcon : DirectionsWalkIcon;
  const unnamed = !visit.patient_name;

  return (
    <Box
      role="link"
      tabIndex={0}
      aria-label={`Open ${visit.display_name}'s visit`}
      onClick={() => onOpen(visit)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(visit);
        }
      }}
      sx={{
        ...ipdCardSx,
        position: "relative",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: COLUMNS },
        alignItems: "center",
        gap: { xs: 1, md: 2 },
        p: 2,
        pl: 3,
        cursor: "pointer",
        transition: "box-shadow .2s ease, border-color .2s ease",
        "&:hover": {
          borderColor: IPD_COLORS.borderHover,
          boxShadow: IPD_COLORS.shadow.cardHover,
        },
        "&:focus-visible": {
          outline: `2px solid ${IPD_COLORS.primary}`,
          outlineOffset: 2,
        },
      }}
    >
      <TriageStripe category={visit.triage_category} />

      {/* Patient */}
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 14,
              color: IPD_COLORS.textPrimary,
              fontStyle: unnamed ? "italic" : "normal",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {visit.display_name}
          </Typography>
          {visit.is_mlc && <MlcBadge caseNumber={visit.mlc_case_number} compact />}
          <Tooltip title={ARRIVAL_MODE_LABEL[visit.arrival_mode] || "Arrival mode not recorded"}>
            <ArrivalIcon sx={{ fontSize: 15, color: IPD_COLORS.textMuted }} />
          </Tooltip>
        </Box>
        <Typography
          sx={{
            mt: 0.25,
            fontSize: 12,
            color: IPD_COLORS.textSecondary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {visit.visit_number} · {visit.age ? `${visit.age}y` : "age ?"}
          {visit.chief_complaint ? ` · ${visit.chief_complaint}` : ""}
        </Typography>
      </Box>

      <Box><TriageBadge category={visit.triage_category} /></Box>

      <Typography sx={{ fontSize: 13, color: visit.bed_label ? IPD_COLORS.textPrimary : IPD_COLORS.textMuted, fontWeight: visit.bed_label ? 600 : 400 }}>
        {visit.bed_label || "No bed"}
      </Typography>

      <Typography
        sx={{
          fontSize: 13,
          color: visit.provider_name ? IPD_COLORS.textPrimary : IPD_COLORS.textMuted,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {visit.provider_name || "Unassigned"}
      </Typography>

      <Typography sx={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: IPD_COLORS.textPrimary }}>
        {waitingSince(visit.arrived_at)}
      </Typography>

      <Box><StageBadge stage={stage} /></Box>

      {/* Triage is the one action worth reaching without opening the record —
          it is what the board exists to prompt. The click must not also
          navigate, hence stopPropagation on the wrapper. */}
      <Box onClick={(e) => e.stopPropagation()} sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 0.75 }}>
        <IpdButton
          size="small"
          variant={visit.triage_category ? "outlined" : "contained"}
          onClick={() => onTriage(visit)}
        >
          {visit.triage_category ? "Re-triage" : "Triage"}
        </IpdButton>
        {/* Offered once triaged and while they have no bed — the next thing
            this patient is waiting for. */}
        {visit.triage_category && !visit.bed_id && (
          <IpdButton size="small" variant="outlined" onClick={() => onAssignBed(visit)}>
            Bed
          </IpdButton>
        )}
      </Box>
    </Box>
  );
}

export default function TrackingBoard() {
  const history = useHistory();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState("");
  const [sortBySeverity, setSortBySeverity] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [triaging, setTriaging] = useState(null);
  const [assigningBed, setAssigningBed] = useState(null);

  // Wait times are derived from `arrived_at` at render time, so the board has
  // to re-render on a timer even when the data itself has not changed.
  const [, setTick] = useState(0);
  const mounted = useRef(true);

  const load = useCallback(async ({ silent } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const { visits: rows } = await listEmergencyVisits({ status: "open" });
      if (!mounted.current) return;
      setVisits(rows);
      setError("");
      setLastUpdated(new Date());
    } catch (e) {
      if (!mounted.current) return;
      // A silent refresh must not wipe a board the department is reading —
      // keep the last good rows on screen and surface the failure alongside.
      setError(e?.message || "Could not load the tracking board");
    } finally {
      if (!mounted.current) return;
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
    };
  }, [load]);

  // ER-BRD-004/005: refresh on a fixed cadence, no live-push connection.
  useEffect(() => {
    const poll = setInterval(() => load({ silent: true }), BOARD_REFRESH_MS);
    const clock = setInterval(() => setTick((t) => t + 1), 30000);
    return () => {
      clearInterval(poll);
      clearInterval(clock);
    };
  }, [load]);

  const rows = useMemo(() => {
    let list = visits;
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((v) =>
        [v.display_name, v.visit_number, v.patient_id, v.chief_complaint]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(q))
      );
    }
    return [...list].sort((a, b) => {
      if (sortBySeverity) {
        const d = triageRank(a.triage_category) - triageRank(b.triage_category);
        if (d !== 0) return d;
      }
      // Longest wait first within the same category (ER-TRI-004).
      return new Date(a.arrived_at) - new Date(b.arrived_at);
    });
  }, [visits, query, sortBySeverity]);

  const stats = useMemo(
    () => ({
      inDepartment: visits.length,
      red: visits.filter((v) => v.triage_category === "red").length,
      awaitingTriage: visits.filter((v) => !v.triage_category).length,
    }),
    [visits]
  );

  return (
    <Box sx={{ ...ipdPageSx, p: { xs: 2, md: 3 } }}>
      <IpdPageHeader
        eyebrow="Emergency Department"
        title="Emergency Tracking Board"
        description="Every patient currently in the department, refreshed automatically."
        badge={loading ? null : `${visits.length} open`}
        descriptionTrailing={
          lastUpdated && (
            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textMuted }}>
              {refreshing ? "Refreshing…" : `Updated ${lastUpdated.toLocaleTimeString()}`}
            </Typography>
          )
        }
        actions={
          <>
            <IpdButton
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => load({ silent: true })}
              loading={refreshing}
            >
              Refresh
            </IpdButton>
            <IpdButton
              variant="outlined"
              startIcon={<FormatListBulletedIcon />}
              onClick={() => history.push("/emergency_all_visits")}
            >
              All visits
            </IpdButton>
            <IpdButton
              startIcon={<PersonAddAlt1Icon />}
              onClick={() => history.push("/emergency_register_patient")}
            >
              Register patient
            </IpdButton>
          </>
        }
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(4, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        <IpdStatCard label="Patients in department" value={stats.inDepartment} icon={PeopleAltIcon} loading={loading} />
        <IpdStatCard label="Red — life-threatening" value={stats.red} icon={MonitorHeartIcon} tone="red" loading={loading} />
        <IpdStatCard label="Awaiting triage" value={stats.awaitingTriage} icon={HourglassTopIcon} tone="amber" loading={loading} />
        <IpdStatCard label="Free beds" value="—" icon={HotelIcon} tone="mint" loading={loading} />
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 2.5, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, visit number, hospital ID or complaint…"
          size="small"
          sx={{ ...ipdElevatedInputSx, flex: 1, minWidth: 260, maxWidth: 420 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: IPD_COLORS.textMuted }} />
              </InputAdornment>
            ),
          }}
          inputProps={{ "aria-label": "Search patients on the tracking board" }}
        />
        <IpdButton
          variant={sortBySeverity ? "contained" : "outlined"}
          startIcon={<SwapVertIcon />}
          onClick={() => setSortBySeverity((s) => !s)}
        >
          {sortBySeverity ? "Sorted by severity" : "Sorted by arrival"}
        </IpdButton>
      </Box>

      {error && (
        <Alert
          severity={visits.length ? "warning" : "error"}
          sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}
          action={
            <IpdButton variant="outlined" size="small" onClick={() => load()}>
              Retry
            </IpdButton>
          }
        >
          {visits.length
            ? `${error} — showing the last successful update.`
            : error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }} />
          ))}
        </Box>
      ) : rows.length === 0 ? (
        <EmergencyPlaceholder
          icon={PeopleAltIcon}
          title={query ? "No matching patients" : "No patients in the department"}
          description={
            query
              ? "Try a different name, visit number or hospital identifier."
              : "The board shows every open emergency visit the moment one is registered."
          }
        />
      ) : (
        <>
          <BoardColumnHeader />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {rows.map((v) => (
              <BoardRow
                key={v.id}
                visit={v}
                onOpen={(visit) => history.push(`/emergency_visit/${visit.id}`)}
                onTriage={setTriaging}
                onAssignBed={setAssigningBed}
              />
            ))}
          </Box>
        </>
      )}

      <Typography sx={{ mt: 3, fontSize: 12, color: IPD_COLORS.textMuted }}>
        The board refreshes on its own every 30 seconds. Updates are periodic rather than
        instantaneous — staff never have to refresh it by hand.
      </Typography>

      <TriageDialog
        open={Boolean(triaging)}
        visit={triaging}
        onClose={() => setTriaging(null)}
        onTriaged={(updated) =>
          // Patch the row in place so the board re-sorts immediately, rather
          // than waiting up to 30s for the next poll to reflect the change.
          setVisits((rows) => rows.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)))
        }
      />

      <AssignBedDialog
        open={Boolean(assigningBed)}
        visit={assigningBed}
        onClose={() => setAssigningBed(null)}
        onAssigned={(updated) => {
          setVisits((rows) => rows.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
          // The free-beds tile is derived from bed state this screen does not
          // hold, so a full reload is the only way to keep it honest.
          load({ silent: true });
        }}
      />
    </Box>
  );
}
